import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UsersService } from '../../services/users.service';
import { ErrorService } from '../../shared/services/error.service';
import { ConfirmationService } from '../../shared/services/confirmation.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css']
})
export class ResetPasswordComponent implements OnInit {
  newPassword: string = '';
  confirmPassword: string = '';

  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  isLoading: boolean = false;

  userEmail: string | null = null;
  verificationCode: string | null = null;
  isTemporaryMode = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private usersService: UsersService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.userEmail = params['email'];
      this.verificationCode = params['code'] || null;
      this.isTemporaryMode = params['mode'] === 'temporary';

      if (!this.userEmail) {
        this.confirmationService.confirm({
          title: 'Erro',
          message: this.isTemporaryMode
            ? 'Email não informado. Por favor, faça login novamente.'
            : 'Email não informado. Por favor, acesse o link enviado por email novamente.',
          confirmText: 'Ir para login',
          cancelText: 'Fechar',
          confirmButtonClass: 'btn-primary',
          cancelButtonClass: 'btn-secondary',
          type: 'danger'
        }).subscribe(confirmed => {
          if (confirmed) {
            this.router.navigate(['/login']);
          }
        });
      }
    });
  }

  get pageTitle(): string {
    return this.isTemporaryMode ? 'Defina sua nova senha' : 'Redefinir Senha';
  }

  get pageInstructions(): string {
    return this.isTemporaryMode
      ? 'Sua senha temporária foi validada. Escolha uma nova senha para continuar.'
      : 'Digite sua nova senha';
  }

  togglePasswordVisibility(field: string) {
    switch (field) {
      case 'newPassword':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirmPassword':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  isFormValid(): boolean {
    if (this.newPassword.trim() === '' || this.confirmPassword.trim() === '') {
      return false;
    }

    if (this.newPassword !== this.confirmPassword) {
      return false;
    }

    if (this.isTemporaryMode) {
      return this.usersService.validatePassword(this.newPassword).isValid;
    }

    return this.newPassword.length >= 6;
  }

  resetPassword() {
    if (!this.isFormValid() || !this.userEmail) {
      this.errorService.showErrorMessage('Por favor, preencha todos os campos corretamente.');
      return;
    }

    if (this.isTemporaryMode) {
      this.resetTemporaryPassword();
      return;
    }

    if (!this.verificationCode) {
      this.confirmationService.confirm({
        title: 'Erro',
        message: 'Código de verificação não informado. Por favor, use o link enviado por email.',
        confirmText: 'OK',
        cancelText: 'Fechar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      }).subscribe();
      return;
    }

    if (this.newPassword.length < 6) {
      this.errorService.showErrorMessage('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errorService.showErrorMessage('As senhas não coincidem.');
      return;
    }

    this.isLoading = true;

    this.authService.passwordCodeValidate(this.userEmail, this.verificationCode, this.newPassword).subscribe({
      next: (resp: { success: boolean; message?: string }) => {
        this.isLoading = false;

        if (resp && resp.success) {
          this.showSuccessAndRedirect();
        } else {
          this.confirmationService.confirm({
            title: 'Erro',
            message: resp?.message || 'Código de verificação inválido ou expirado. Tente novamente.',
            confirmText: 'OK',
            cancelText: 'Fechar',
            confirmButtonClass: 'btn-primary',
            cancelButtonClass: 'btn-secondary',
            type: 'danger'
          }).subscribe();
        }
      },
      error: () => {
        this.isLoading = false;
        this.confirmationService.confirm({
          title: 'Erro',
          message: 'Erro ao alterar senha. Tente novamente.',
          confirmText: 'OK',
          cancelText: 'Fechar',
          confirmButtonClass: 'btn-primary',
          cancelButtonClass: 'btn-secondary',
          type: 'danger'
        }).subscribe();
      }
    });
  }

  private resetTemporaryPassword(): void {
    const validation = this.usersService.validatePassword(this.newPassword);
    if (!validation.isValid) {
      this.errorService.showErrorMessage(validation.errors.join('. '));
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errorService.showErrorMessage('As senhas não coincidem.');
      return;
    }

    this.isLoading = true;

    this.usersService.changePasswordAfterTemporary(
      this.userEmail!,
      this.newPassword,
      this.userEmail!
    ).subscribe({
      next: (resp) => {
        this.isLoading = false;

        if (resp?.success !== false) {
          this.showSuccessAndRedirect();
        } else {
          this.confirmationService.confirm({
            title: 'Erro',
            message: resp?.message || 'Erro ao alterar senha. Tente novamente.',
            confirmText: 'OK',
            cancelText: 'Fechar',
            confirmButtonClass: 'btn-primary',
            cancelButtonClass: 'btn-secondary',
            type: 'danger'
          }).subscribe();
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private showSuccessAndRedirect(): void {
    this.clearForm();

    this.confirmationService.confirm({
      title: 'Sucesso',
      message: 'Senha alterada com sucesso!',
      confirmText: 'Ir para login',
      cancelText: 'Fechar',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'btn-secondary',
      type: 'info'
    }).subscribe(confirmed => {
      if (confirmed) {
        this.router.navigate(['/login']);
      }
    });
  }

  clearForm() {
    this.newPassword = '';
    this.confirmPassword = '';
    this.showNewPassword = false;
    this.showConfirmPassword = false;
  }

  goBack() {
    this.router.navigate(['/login']);
  }
}
